import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { Product } from 'Common/types';
import * as SearchApi from 'Common/features/search/api';
import { useTranslation, useViewport } from 'Common/hooks';
import { Loader } from 'Common/components/ui';
import { SparePartsPartial } from 'Common/containers/product';
import { SparePartsDropdown, SparePartsTable } from '../SpareParts';

export default function SparePartsFinder({
    categories,
    className,
    desktopImage,
    mobileImage,
    style,
    clearItemDetails,
    currentProduct,
    fetchProductDetails,
    fetchProducts,
    fetchSpareParts,
    partsLoading,
    products,
    selectedCode,
    selectedProduct,
    setItemDetails,
    setSelected,
    spareParts,
    variants,
}) {
    const imageAlt = useTranslation('SpareParts.Table.Placeholder.Image.AltText');
    const categoryLbl = useTranslation('SpareParts.Finder.Category.Dropdown.Label');
    const productLbl = useTranslation('SpareParts.Finder.Product.Dropdown.Label');
    const noProductsLbl = useTranslation('SpareParts.Finder.Product.Dropdown.Empty.Label');
    const variantLbl = useTranslation('SpareParts.Finder.Variant.Dropdown.Label');
    const noVariantsLbl = useTranslation('SpareParts.Finder.Variant.Dropdown.Empty.Label');
    const searchPlaceholder = useTranslation('SpareParts.Finder.Search.Input.Placeholder');
    const resetBtnText = useTranslation('SpareParts.Finder.Reset.Button.Text');
    const searchBtnText = useTranslation('SpareParts.Finder.Search.Button.Text');
    const noResultsText = useTranslation('SpareParts.Finder.Search.Results.Empty.Text');

    const viewport = useViewport();
    const dropdownRef = useRef(null);

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [productOptions, setProductOptions] = useState(null);
    const [selectedProductOption, setSelectedProductOption] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [oneOffPart, setOneOffPart] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [noResults, setNoResults] = useState(false);

    const mappedChildren = useMemo(() => {
        if (!variants) return [];
        const arr = Array.isArray(variants) ? variants : [variants];

        return arr.map((c) => ({ Name: c.DisplayName, Id: c.Code }));
    }, [variants]);

    const showPlaceholder = !selectedVariant && !spareParts?.length && !oneOffPart;
    const showTable = Boolean(spareParts?.length);

    const clearAllSelections = useCallback(() => {
        setSelectedCategory(null);
        setProductOptions(null);
        setSelectedProductOption(null);
        setSelectedVariant(null);
        setOneOffPart(null);
        setSearchTerm('');
        setSearchResults([]);
        setNoResults(false);
        setSelected('');
        clearItemDetails();
    }, [clearItemDetails, setSelected]);

    const onReset = useCallback(() => {
        clearAllSelections();
    }, [clearAllSelections]);

    useEffect(() => {
        if (selectedCategory) {
            fetchProducts(selectedCategory.Id);
            setSelectedProductOption(null);
            setSelectedVariant(null);
            setOneOffPart(null);
        }
    }, [selectedCategory, fetchProducts]);

    useEffect(() => {
        if (products?.length) {
            setProductOptions(products);
        }
    }, [products]);

    useEffect(() => {
        if (!selectedCode) return;

        if (currentProduct?.Code !== selectedCode) {
            if (!selectedProduct) {
                fetchProductDetails(selectedCode);
            } else {
                setItemDetails({
                    Product: JSON.parse(JSON.stringify(selectedProduct)),
                    ProductsByNode: products,
                });
            }
        }
    }, [selectedCode, currentProduct, selectedProduct, products, fetchProductDetails, setItemDetails]);

    useEffect(() => {
        if (selectedProductOption) {
            setSelected(selectedProductOption.Id);
            setSelectedVariant(null);
            setOneOffPart(null);
            clearItemDetails();
        }
    }, [selectedProductOption, setSelected, clearItemDetails]);

    useEffect(() => {
        if (selectedVariant) {
            fetchSpareParts(selectedVariant.Id);
            setOneOffPart(null);
        }
    }, [selectedVariant, fetchSpareParts]);

    useEffect(() => {
        if (!searchTerm) {
            setSearchResults([]);
            setNoResults(false);
        }
    }, [searchTerm]);

    useEffect(() => {
        if (!noResults) return;
        const timer = setTimeout(() => {
            setNoResults(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, [noResults]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setSearchResults([]);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const onSearchInput = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    const onSearch = useCallback(() => {
        if (!searchTerm.trim()) return;

        let cancelled = false;

        setIsSearching(true);

        (async () => {
            try {
                const resp = await SearchApi.itemSearch(searchTerm);

                if (!cancelled && resp.success) {
                    if (resp.Results.length) {
                        setSearchResults(resp.Results);
                        setNoResults(false);
                    } else {
                        setSearchResults([]);
                        setNoResults(true);
                    }
                }
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setIsSearching(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [searchTerm]);

    const onSelectHit = useCallback(
        (hit) => {
            setSearchResults([]);
            setSearchTerm('');
            setNoResults(false);

            if (hit.SparePartCodes?.length > 0) {
                setOneOffPart(null);
                setSelectedVariant({ Id: hit.ProductCode, Name: hit.Title });
            } else {
                setOneOffPart(hit.ProductInfo);
                setSelectedVariant(null);
            }

            clearItemDetails();
            setSelectedCategory(null);
            setSelectedProductOption(null);
            setSelected('');
        },
        [clearItemDetails, setSelected]
    );

    return (
        <div className="SparePartsFinder" style={style}>
            {(partsLoading || isSearching) && <Loader />}

            <div className="SparePartsFinder__Dropdowns">
                <SparePartsDropdown
                    options={categories}
                    className={className}
                    placeholder={categoryLbl}
                    selected={selectedCategory}
                    setSelected={setSelectedCategory}
                />

                <SparePartsDropdown
                    options={productOptions ?? []}
                    className={className}
                    disabled={!productOptions?.length}
                    placeholder={
                        selectedCategory && !partsLoading && !productOptions?.length ? noProductsLbl : productLbl
                    }
                    selected={selectedProductOption}
                    setSelected={setSelectedProductOption}
                    showId
                />

                <SparePartsDropdown
                    options={mappedChildren}
                    className={className}
                    disabled={!mappedChildren.length}
                    placeholder={
                        selectedProductOption && !partsLoading && !mappedChildren.length ? noVariantsLbl : variantLbl
                    }
                    selected={selectedVariant}
                    setSelected={setSelectedVariant}
                    showId
                />

                {(selectedCategory || selectedVariant || oneOffPart) && (
                    <div className="SparePartsFinder__Dropdowns__Reset">
                        <a className="button" onClick={onReset}>
                            {resetBtnText}
                        </a>
                    </div>
                )}
            </div>

            <br />

            <div className="d-flex">
                <div className="d-flex">
                    <input
                        type="text"
                        className="form-control me-2"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={onSearchInput}
                    />
                    <button className="btn-primary" onClick={onSearch}>
                        {searchBtnText}
                    </button>
                </div>
            </div>

            <div className="position-relative" ref={dropdownRef}>
                {(searchResults.length > 0 || noResults) && (
                    <ul className="list-group position-absolute w-100" style={{ zIndex: 10 }}>
                        {noResults && (
                            <li className="list-group-item list-group-item-action d-flex align-items-center">
                                {noResultsText}
                            </li>
                        )}
                        {searchResults.map((hit) => (
                            <li
                                key={hit.ProductCode}
                                className="list-group-item list-group-item-action d-flex align-items-center"
                                role="button"
                                onClick={() => onSelectHit(hit)}
                            >
                                <img
                                    src={hit.DefaultImageUrl}
                                    alt={hit.Title}
                                    style={{
                                        width: 40,
                                        height: 40,
                                        objectFit: 'cover',
                                        marginRight: 8,
                                        borderRadius: 4,
                                    }}
                                />
                                <div>
                                    <div className="font-weight-bold">{hit.ProductCode}</div>
                                    <div>{hit.Title}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <br />

            <div className="SparePartsFinder__Main">
                {showPlaceholder && (
                    <div className="SparePartsFinder__Main__Placeholder">
                        <img
                            className={cx(
                                'SparePartsFinder__Main__Placeholder-img',
                                spareParts?.length ? 'fade-out' : 'fade-in'
                            )}
                            src={viewport.is.lt('md') && mobileImage ? mobileImage : desktopImage}
                            alt={imageAlt}
                        />
                    </div>
                )}

                {oneOffPart ? (
                    <div className="mb-4">
                        <SparePartsPartial sparePartsData={{ Product: oneOffPart }} />
                    </div>
                ) : (
                    showTable && (
                        <div className="mb-4">
                            <SparePartsTable spareParts={spareParts} />
                        </div>
                    )
                )}
                {selectedVariant && !spareParts?.length && <div>{noResultsText}</div>}
            </div>
        </div>
    );
}

SparePartsFinder.propTypes = {
    categories: px.arrayOf(
        px.shape({
            Name: px.string,
            Id: px.oneOfType([px.string, px.number]),
        })
    ),
    className: px.string,
    clearItemDetails: px.func,
    currentProduct: Product,
    desktopImage: px.string,
    fetchProductDetails: px.func,
    fetchProducts: px.func,
    fetchSpareParts: px.func,
    mobileImage: px.string,
    partsLoading: px.bool,
    products: px.arrayOf(Product),
    selectedCode: px.string,
    selectedProduct: Product,
    setItemDetails: px.func,
    setSelected: px.func,
    spareParts: px.arrayOf(Product),
    style: px.object,
    variants: px.arrayOf(Product),
};
